"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Activity, Sparkles, Microscope, PawPrint, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const expertiseItems = [{
  id: "01",
  icon: Activity,
  title: "Surgical & Diagnostic",
  subtitle: "Precision clinical instrumentation.",
  description: "We source high-performance surgical tools and advanced diagnostic systems for superior patient outcomes.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_diagnostic.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_diagnostic.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/surgical_diagnostic.png-wvc-sizes",
  imageAlt: "Surgical and diagnostic equipment in a clinical setting",
  link: "/SolutionsPage"
}, {
  id: "02",
  icon: Sparkles,
  title: "Aesthetic Fillers & Exosome Technology",
  subtitle: "Regenerative aesthetic solutions.",
  description: "Our portfolio includes cutting-edge aesthetic and regenerative technologies for non-invasive clinical treatments.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/aesthetic_fillers.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/aesthetic_fillers.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/aesthetic_fillers.png-wvc-sizes",
  imageAlt: "Close up of a woman's face for aesthetic treatments",
  link: "/SolutionsPage"
}, {
  id: "03",
  icon: Microscope,
  title: "Medical-Grade Skincare & Dermatology",
  subtitle: "Advanced dermatological platforms.",
  description: "We distribute medical-grade skincare formulations and dermatological devices that enhance skin health through science.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_skincare.webp",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_skincare.webp-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/medical_skincare.webp-wvc-sizes",
  imageAlt: "Medical grade skincare products on a tray",
  link: "/SolutionsPage"
}, {
  id: "04",
  icon: PawPrint,
  title: "Veterinary Care Solutions",
  subtitle: "Specialized animal healthcare tools.",
  description: "Providing veterinary clinics with high-standard surgical and diagnostic equipment designed for animal care.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/veterinary_care.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/veterinary_care.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1727079/img/veterinary_care.png-wvc-sizes",
  imageAlt: "Veterinarians examining a dog in a clinic",
  link: "/SolutionsPage"
}];
export default function OurExpertise() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };
  return <section id="ourexpertise" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="container mx-auto px-6 max-w-7xl">
        {}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <motion.span initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1]
        }} className="text-xs uppercase tracking-wide text-primary mb-4 block">
            Our Expertise
          </motion.span>
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease: [0.4, 0, 0.2, 1],
          delay: 0.1
        }} className="text-6xl font-default font-bold text-foreground leading-tight">
            Global Sourcing for <br className="hidden md:block" />
            <span className="text-primary">Innovative Healthcare.</span>
          </motion.h2>
        </div>

        {}
        <motion.div ref={ref} variants={containerVariants} initial="hidden" animate={isVisible ? "visible" : "hidden"} className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
          {expertiseItems.map((item, i) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={i} variants={itemVariants} className="bg-card rounded-2xl shadow-sm hover:shadow-xl transition-smooth p-6 md:p-8 lg:p-10 flex flex-col h-full border border-border/40 group">
                {}
                <div className="flex justify-between items-center mb-8">
                  
                  <span className="text-primary font-bold text-sm tracking-widest font-mono">
                    N° .{item.id}
                  </span>
                </div>

                {}
                <div className="mb-8 overflow-hidden rounded-xl">
                  <AspectRatio ratio={4 / 3}>
                    <img src={item.imageSrc} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} className="object-cover w-full h-full group-hover:scale-105 transition-transform duration-700 ease-out" loading="lazy" />
                  </AspectRatio>
                </div>

                {}
                <div className="flex-grow flex flex-col">
                  <h3 className="text-2xl font-default font-bold text-foreground mb-3">
                    {item.title}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-5">
                    {item.subtitle}
                  </p>
                  <p className="text-sm text-muted-foreground leading-normal mb-8 flex-grow">
                    {item.description}
                  </p>

                  {}
                  <div className="mt-auto pt-4 border-t border-border/50">
                    
                  </div>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}